import { useCallback, useEffect, useRef } from "react";
import { usePreload } from "../DataContext/PreloadContext";

// The app uses <BrowserRouter>, so react-router's useBlocker isn't available.
// Instead this guards in-app link clicks (breadcrumbs, sidebar, Link buttons)
// and tab close/refresh. Browser back/forward is not intercepted.
// Pass interceptLinks: false when pending changes survive in-app navigation
// (e.g. kept in a page cache) and only a full reload would lose them.
export const useUnsavedChangesGuard = (
  when,
  { interceptLinks = true } = {},
) => {
  const { confirmDialog } = usePreload();
  const confirmRef = useRef(confirmDialog);
  confirmRef.current = confirmDialog;
  const bypassRef = useRef(false);

  // resolves true when it's fine to leave (no changes, or the user confirmed)
  const confirmLeave = useCallback(async () => {
    if (!when) return true;
    const ok = await confirmRef.current.show({
      title: "Unsaved Changes",
      message: `Unsaved changes detected, Would you like to cancel?`,
    });
    confirmRef.current.close();
    return ok;
  }, [when]);

  useEffect(() => {
    if (!when) return;

    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const onClick = async (e) => {
      if (bypassRef.current) return;
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = e.target.closest?.("a[href]");
      if (!anchor || (anchor.target && anchor.target !== "_self")) return;
      if (anchor.hasAttribute("download")) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname) return;

      e.preventDefault();
      e.stopPropagation();

      if (await confirmLeave()) {
        // replay the click so the link's own handlers (e.g. closing the drawer) still run
        bypassRef.current = true;
        try {
          anchor.click();
        } finally {
          bypassRef.current = false;
        }
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    if (interceptLinks) document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [when, confirmLeave, interceptLinks]);

  return { confirmLeave };
};
